
/**
 * Producer Queue Management Hook
 *
 * This hook manages the live service display queues for producers during
 * church services. It provides real-time queue state with WebSocket updates
 * and controls for managing what appears on screen.
 *
 * Queue Types:
 *   - prayer: Prayer requests queue
 *   - praise: Answered prayers queue
 *
 * Features:
 *   - Real-time queue updates via WebSocket
 *   - Mark items as displayed
 *   - Remove items from queue
 *   - Group items by category
 *   - Export to ProPresenter format
 *   - Auto-refresh fallback
 *
 * Producer Workflow:
 *   1. Producer dashboard loads one queue at a time; Live Display may load both (`activeQueue: 'both'`)
 *   2. Mark items as displayed when shown on screen
 *   3. Remove items or reorder as needed
 *   4. Export to ProPresenter for professional display
 *
 * @example
 * function ProducerDashboard() {
 *   const {
 *     prayerQueue,
 *     praiseQueue,
 *     markDisplayed,
 *     exportQueue
 *   } = useProducerQueue({ token: authToken });
 *
 *   const handleExport = async () => {
 *     const text = await exportQueue('all', { groupByCategory: true });
 *     await navigator.clipboard.writeText(text);
 *   };
 *
 *   // Render queues...
 * }
 *
 * @module hooks/useProducerQueue
 */

import { useEffect, useCallback, useMemo, useRef, useReducer } from 'react';
import { api, getCurrentLive, getShuffleState, putShuffleState, type ShuffleStateUpdate } from '@/lib/api';
import { formatFullName } from '@/lib/format';
import { getPrayerCategories } from '@/lib/categories';
import { logger } from '@/lib/logger';
import { useWebSocket, WSEventType, ConnectionState } from './useWebSocket';
import type { PrayerRequest, PrayerType } from '@/types';
import { PrayerStatus } from '@/types';

/**
 * An item in the display queue.
 */
export interface QueueItem {
  /** Queue entry ID */
  id: string;
  /** Referenced prayer request ID */
  prayerRequestId: string;
  /** Full prayer request data */
  prayerRequest: PrayerRequest;
  /** Position in queue (1-indexed) */
  position: number;
  /** Which queue this belongs to */
  queueType: 'prayer' | 'praise';
  /** When added to queue */
  createdAt: string;
  /** When displayed on screen (null if not yet) */
  displayedAt: string | null;
  /** Whether this has been shown on screen */
  isDisplayed: boolean;
  /** Number of times this item has been displayed */
  displayCount?: number;
}

/**
 * Queue items organized by category.
 */
export interface GroupedQueueItems {
  [category: string]: QueueItem[];
}

function isFullQueueItemPayload(data: unknown): data is QueueItem {
  if (data == null || typeof data !== 'object') return false;
  const pr = (data as QueueItem).prayerRequest;
  return pr != null && typeof pr === 'object';
}

/**
 * Options for the producer queue hook.
 */
interface UseProducerQueueOptions {
  /** JWT token for authenticated requests */
  token?: string;
  /** Enable periodic refresh as fallback (default: true) */
  autoRefresh?: boolean;
  /** Refresh interval in ms (default: 30000) */
  refreshInterval?: number;
  /** `prayer` / `praise`: fetch that queue only. `combined` / `both`: fetch both queues. */
  activeQueue?: 'prayer' | 'praise' | 'combined' | 'both';
}

/**
 * Return type for the producer queue hook.
 */
interface UseProducerQueueReturn {
  /** Prayer requests queue */
  prayerQueue: QueueItem[];
  /** Answered prayers queue */
  praiseQueue: QueueItem[];
  /** Prayer queue grouped by category */
  prayerQueueGrouped: GroupedQueueItems;
  /** Answered prayers queue grouped by category */
  praiseQueueGrouped: GroupedQueueItems;
  /** Whether queues are loading */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** WebSocket connection status */
  isConnected: boolean;
  /** Detailed connection state */
  connectionState: ConnectionState;
  /** Timestamp of last queue update */
  lastUpdate: Date | null;
  /** Count of new items since last view */
  newItemsCount: number;
  /** Mark an item as displayed on screen */
  markDisplayed: (itemId: string) => Promise<boolean>;
  /** Remove an item from its queue */
  removeFromQueue: (prayerId: string) => Promise<boolean>;
  /** Remove displayed cards from the queue; pending (not yet shown) items stay */
  clearQueue: (queueType: 'prayer' | 'praise') => Promise<boolean>;
  /** Manually refresh queues */
  refresh: () => Promise<void>;
  /** Clear the new items counter */
  clearNewItems: () => void;
  /** Export queue(s) to text format for ProPresenter */
  exportQueue: (type: 'prayer' | 'praise' | 'all', options?: ExportOptions) => Promise<string>;
  /** Get export text synchronously (for clipboard copy in same user gesture) */
  getExportText: (type: 'prayer' | 'praise' | 'all', options?: ExportOptions) => string;
  /** Server-persisted shuffle state */
  shufflePrayer: boolean;
  shufflePraise: boolean;
  /** Prayer queue shuffle interval (seconds). */
  shufflePrayerIntervalSeconds: number;
  /** Praise queue shuffle interval (seconds). */
  shufflePraiseIntervalSeconds: number;
  /** Combined mix shuffle on/off */
  shuffleCombined: boolean;
  /** Combined mix shuffle interval (seconds). */
  shuffleCombinedIntervalSeconds: number;
  /** Update shuffle state (persists on server, syncs to all producers) */
  setShuffleState: (update: ShuffleStateUpdate) => Promise<void>;
  /** Which prayer request is currently live (for Live badge); updated by PRAYER_DISPLAYED and manual set */
  livePrayerRequestId: string | null;
  /** Which praise is currently live; updated by PRAYER_DISPLAYED and manual set */
  livePraiseRequestId: string | null;
  /** Which item is live on the combined channel */
  liveCombinedRequestId: string | null;
  /** Queue type of the combined live item (`prayer` | `praise`) */
  liveCombinedQueueType: 'prayer' | 'praise' | null;
  /** Which prayer the shuffle will show next (for "Next in shuffle" indicator); synced from GET current-live */
  shufflePrayerNextRequestId: string | null;
  /** Which praise the shuffle will show next; synced from GET current-live */
  shufflePraiseNextRequestId: string | null;
  /** Next 2 prayer IDs the shuffle will show (for Up Next preview) */
  shufflePrayerNextIds: string[];
  /** Next 2 praise IDs the shuffle will show (for Up Next preview) */
  shufflePraiseNextIds: string[];
  /** Next combined picks for Up Next preview */
  shuffleCombinedNextIds: Array<{ id: string; queueType: 'prayer' | 'praise' | string }>;
  /** True when current live prayer was set by user clicking Display Live (show "Next in shuffle" only then) */
  liveIsManualOverridePrayer: boolean;
  /** True when current live praise was set by user clicking Display Live */
  liveIsManualOverridePraise: boolean;
  /** Set current live prayer (optimistic update when producer clicks Display Live) */
  setLivePrayerRequestId: (id: string | null) => void;
  /** Set current live praise (optimistic update when producer clicks Display Live) */
  setLivePraiseRequestId: (id: string | null) => void;
  /** Set current combined live id (optimistic) */
  setLiveCombinedRequestId: (id: string | null) => void;
  /** Call after user manually displays a prayer (so "Next in shuffle" is shown until shuffle advances) */
  markLiveAsManualOverridePrayer: () => void;
  /** Call after user manually displays a praise */
  markLiveAsManualOverridePraise: () => void;
  /** Prayer ID -> { userId, userName } for who is currently editing (real-time, same as Submissions) */
  editingBy: Record<string, { userId: string; userName: string }>;
  /** WebSocket reconnection counter (refetch queues when it increments) */
  reconnectedTick: number;
  /** Last JSON pong timestamp (ms); for producer connection health UI */
  lastPongAt: number | null;
  /** Send a raw WebSocket message (e.g. start_editing / stop_editing) */
  sendMessage: (message: object) => void;
}

/**
 * Options for queue export.
 */
interface ExportOptions {
  /** Group output by category */
  groupByCategory?: boolean;
  /** Include category labels in output */
  includeCategoryLabels?: boolean;
}

type Updater<T> = T | ((prev: T) => T);

interface ProducerState {
  prayerQueue: QueueItem[];
  praiseQueue: QueueItem[];
  isLoading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  newItemsCount: number;
  shufflePrayer: boolean;
  shufflePraise: boolean;
  shuffleCombined: boolean;
  shufflePrayerIntervalSeconds: number;
  shufflePraiseIntervalSeconds: number;
  shuffleCombinedIntervalSeconds: number;
  livePrayerRequestId: string | null;
  livePraiseRequestId: string | null;
  liveCombinedRequestId: string | null;
  liveCombinedQueueType: 'prayer' | 'praise' | null;
  shufflePrayerNextRequestId: string | null;
  shufflePraiseNextRequestId: string | null;
  shufflePrayerNextIds: string[];
  shufflePraiseNextIds: string[];
  shuffleCombinedNextIds: Array<{ id: string; queueType: 'prayer' | 'praise' | string }>;
  liveIsManualOverridePrayer: boolean;
  liveIsManualOverridePraise: boolean;
  editingBy: Record<string, { userId: string; userName: string }>;
}

type ProducerAction =
  | { type: 'set_prayer_queue'; payload: Updater<QueueItem[]> }
  | { type: 'set_praise_queue'; payload: Updater<QueueItem[]> }
  | { type: 'set_is_loading'; payload: boolean }
  | { type: 'set_error'; payload: string | null }
  | { type: 'set_last_update'; payload: Date | null }
  | { type: 'set_new_items_count'; payload: Updater<number> }
  | { type: 'set_shuffle_prayer'; payload: boolean }
  | { type: 'set_shuffle_praise'; payload: boolean }
  | { type: 'set_shuffle_combined'; payload: boolean }
  | { type: 'set_shuffle_prayer_interval_seconds'; payload: number }
  | { type: 'set_shuffle_praise_interval_seconds'; payload: number }
  | { type: 'set_shuffle_combined_interval_seconds'; payload: number }
  | { type: 'set_live_prayer_request_id'; payload: string | null }
  | { type: 'set_live_praise_request_id'; payload: string | null }
  | { type: 'set_live_combined_request_id'; payload: string | null }
  | { type: 'set_live_combined_queue_type'; payload: 'prayer' | 'praise' | null }
  | { type: 'set_shuffle_prayer_next_request_id'; payload: string | null }
  | { type: 'set_shuffle_praise_next_request_id'; payload: string | null }
  | { type: 'set_shuffle_prayer_next_ids'; payload: string[] }
  | { type: 'set_shuffle_praise_next_ids'; payload: string[] }
  | { type: 'set_shuffle_combined_next_ids'; payload: Array<{ id: string; queueType: 'prayer' | 'praise' | string }> }
  | { type: 'set_live_is_manual_override_prayer'; payload: boolean }
  | { type: 'set_live_is_manual_override_praise'; payload: boolean }
  | { type: 'set_editing_by'; payload: Updater<ProducerState['editingBy']> };

const initialProducerState: ProducerState = {
  prayerQueue: [],
  praiseQueue: [],
  isLoading: true,
  error: null,
  lastUpdate: null,
  newItemsCount: 0,
  shufflePrayer: false,
  shufflePraise: false,
  shuffleCombined: false,
  shufflePrayerIntervalSeconds: 15,
  shufflePraiseIntervalSeconds: 15,
  shuffleCombinedIntervalSeconds: 15,
  livePrayerRequestId: null,
  livePraiseRequestId: null,
  liveCombinedRequestId: null,
  liveCombinedQueueType: null,
  shufflePrayerNextRequestId: null,
  shufflePraiseNextRequestId: null,
  shufflePrayerNextIds: [],
  shufflePraiseNextIds: [],
  shuffleCombinedNextIds: [],
  liveIsManualOverridePrayer: false,
  liveIsManualOverridePraise: false,
  editingBy: {},
};

function applyUpdater<T>(current: T, payload: Updater<T>): T {
  return typeof payload === 'function' ? (payload as (prev: T) => T)(current) : payload;
}

/** Drop rows missing embedded prayer data so UI never renders undefined.prayerRequest */
function filterValidQueueItems(items: QueueItem[]): QueueItem[] {
  return items.filter(
    (i) => i.prayerRequest != null && typeof i.prayerRequest === 'object'
  );
}

function producerReducer(state: ProducerState, action: ProducerAction): ProducerState {
  switch (action.type) {
    case 'set_prayer_queue':
      return {
        ...state,
        prayerQueue: filterValidQueueItems(applyUpdater(state.prayerQueue, action.payload)),
      };
    case 'set_praise_queue':
      return {
        ...state,
        praiseQueue: filterValidQueueItems(applyUpdater(state.praiseQueue, action.payload)),
      };
    case 'set_is_loading':
      return { ...state, isLoading: action.payload };
    case 'set_error':
      return { ...state, error: action.payload };
    case 'set_last_update':
      return { ...state, lastUpdate: action.payload };
    case 'set_new_items_count':
      return { ...state, newItemsCount: applyUpdater(state.newItemsCount, action.payload) };
    case 'set_shuffle_prayer':
      return { ...state, shufflePrayer: action.payload };
    case 'set_shuffle_praise':
      return { ...state, shufflePraise: action.payload };
    case 'set_shuffle_combined':
      return { ...state, shuffleCombined: action.payload };
    case 'set_shuffle_prayer_interval_seconds':
      return { ...state, shufflePrayerIntervalSeconds: action.payload };
    case 'set_shuffle_praise_interval_seconds':
      return { ...state, shufflePraiseIntervalSeconds: action.payload };
    case 'set_shuffle_combined_interval_seconds':
      return { ...state, shuffleCombinedIntervalSeconds: action.payload };
    case 'set_live_prayer_request_id':
      return { ...state, livePrayerRequestId: action.payload };
    case 'set_live_praise_request_id':
      return { ...state, livePraiseRequestId: action.payload };
    case 'set_live_combined_request_id':
      return { ...state, liveCombinedRequestId: action.payload };
    case 'set_live_combined_queue_type':
      return { ...state, liveCombinedQueueType: action.payload };
    case 'set_shuffle_prayer_next_request_id':
      return { ...state, shufflePrayerNextRequestId: action.payload };
    case 'set_shuffle_praise_next_request_id':
      return { ...state, shufflePraiseNextRequestId: action.payload };
    case 'set_shuffle_prayer_next_ids':
      return { ...state, shufflePrayerNextIds: action.payload };
    case 'set_shuffle_praise_next_ids':
      return { ...state, shufflePraiseNextIds: action.payload };
    case 'set_shuffle_combined_next_ids':
      return { ...state, shuffleCombinedNextIds: action.payload };
    case 'set_live_is_manual_override_prayer':
      return { ...state, liveIsManualOverridePrayer: action.payload };
    case 'set_live_is_manual_override_praise':
      return { ...state, liveIsManualOverridePraise: action.payload };
    case 'set_editing_by':
      return { ...state, editingBy: applyUpdater(state.editingBy, action.payload) };
    default:
      return state;
  }
}

/**
 * Hook for managing producer display queues with real-time updates.
 *
 * @param options - Configuration options
 * @returns Queue state and management functions
 */
export function useProducerQueue(options: UseProducerQueueOptions = {}): UseProducerQueueReturn {
  const { token, autoRefresh = true, refreshInterval = 30000, activeQueue: activeQueueOpt = 'prayer' } = options;

  const [state, dispatch] = useReducer(producerReducer, initialProducerState);
  const {
    prayerQueue,
    praiseQueue,
    isLoading,
    error,
    lastUpdate,
    newItemsCount,
    shufflePrayer,
    shufflePraise,
    shuffleCombined,
    shufflePrayerIntervalSeconds,
    shufflePraiseIntervalSeconds,
    shuffleCombinedIntervalSeconds,
    livePrayerRequestId,
    livePraiseRequestId,
    liveCombinedRequestId,
    liveCombinedQueueType,
    shufflePrayerNextRequestId,
    shufflePraiseNextRequestId,
    shufflePrayerNextIds,
    shufflePraiseNextIds,
    shuffleCombinedNextIds,
    liveIsManualOverridePrayer,
    liveIsManualOverridePraise,
    editingBy,
  } = state;

  // Keep existing setter names so the rest of the hook logic can stay readable.
  const setPrayerQueue = (payload: Updater<QueueItem[]>) =>
    dispatch({ type: 'set_prayer_queue', payload });
  const setPraiseQueue = (payload: Updater<QueueItem[]>) =>
    dispatch({ type: 'set_praise_queue', payload });
  const setIsLoading = (payload: boolean) => dispatch({ type: 'set_is_loading', payload });
  const setError = (payload: string | null) => dispatch({ type: 'set_error', payload });
  const setLastUpdate = (payload: Date | null) => dispatch({ type: 'set_last_update', payload });
  const setNewItemsCount = (payload: Updater<number>) =>
    dispatch({ type: 'set_new_items_count', payload });
  const setShufflePrayer = (payload: boolean) => dispatch({ type: 'set_shuffle_prayer', payload });
  const setShufflePraise = (payload: boolean) => dispatch({ type: 'set_shuffle_praise', payload });
  const setShuffleCombined = (payload: boolean) => dispatch({ type: 'set_shuffle_combined', payload });
  const setShufflePrayerIntervalSeconds = (payload: number) =>
    dispatch({ type: 'set_shuffle_prayer_interval_seconds', payload });
  const setShufflePraiseIntervalSeconds = (payload: number) =>
    dispatch({ type: 'set_shuffle_praise_interval_seconds', payload });
  const setShuffleCombinedIntervalSeconds = (payload: number) =>
    dispatch({ type: 'set_shuffle_combined_interval_seconds', payload });
  const setLivePrayerRequestId = (payload: string | null) =>
    dispatch({ type: 'set_live_prayer_request_id', payload });
  const setLivePraiseRequestId = (payload: string | null) =>
    dispatch({ type: 'set_live_praise_request_id', payload });
  const setLiveCombinedRequestId = (payload: string | null) =>
    dispatch({ type: 'set_live_combined_request_id', payload });
  const setLiveCombinedQueueType = (payload: 'prayer' | 'praise' | null) =>
    dispatch({ type: 'set_live_combined_queue_type', payload });
  const setShufflePrayerNextRequestId = (payload: string | null) =>
    dispatch({ type: 'set_shuffle_prayer_next_request_id', payload });
  const setShufflePraiseNextRequestId = (payload: string | null) =>
    dispatch({ type: 'set_shuffle_praise_next_request_id', payload });
  const setShufflePrayerNextIds = (payload: string[]) =>
    dispatch({ type: 'set_shuffle_prayer_next_ids', payload });
  const setShufflePraiseNextIds = (payload: string[]) =>
    dispatch({ type: 'set_shuffle_praise_next_ids', payload });
  const setShuffleCombinedNextIds = (
    payload: Array<{ id: string; queueType: 'prayer' | 'praise' | string }>
  ) => dispatch({ type: 'set_shuffle_combined_next_ids', payload });
  const setLiveIsManualOverridePrayer = (payload: boolean) =>
    dispatch({ type: 'set_live_is_manual_override_prayer', payload });
  const setLiveIsManualOverridePraise = (payload: boolean) =>
    dispatch({ type: 'set_live_is_manual_override_praise', payload });
  const setEditingBy = (payload: Updater<ProducerState['editingBy']>) =>
    dispatch({ type: 'set_editing_by', payload });

  /** Prayer IDs we just removed via WebSocket; ignore them if a stale fetch returns them (guard window 5s) */
  const recentlyRemovedRef = useRef<Record<string, number>>({});

  // WebSocket connection for real-time updates
  const { isConnected, connectionState, messageQueueTick, drainQueuedMessages, sendMessage, reconnectedTick, lastPongAt } = useWebSocket({
    token,
    rooms: ['producer', 'queue'],
    autoReconnect: true,
  });

  // Group items by category (multi-tag: same item may appear under every selected category)
  const groupByCategory = useCallback((items: QueueItem[]): GroupedQueueItems => {
    return items.reduce((acc, item) => {
      const cats = getPrayerCategories(item.prayerRequest ?? {});
      const labels = cats.length > 0 ? cats : ['General'];
      for (const category of labels) {
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
      }
      return acc;
    }, {} as GroupedQueueItems);
  }, []);

  const prayerQueueGrouped = useMemo(() => groupByCategory(prayerQueue), [prayerQueue, groupByCategory]);
  const praiseQueueGrouped = useMemo(() => groupByCategory(praiseQueue), [praiseQueue, groupByCategory]);

  // Normalize API queue items: isDisplayed from displayedAt, displayCount from API (snake_case or camelCase)
  const normalizeQueueItems = useCallback((items: QueueItem[]): QueueItem[] => {
    return items.map((item) => {
      const raw = item as QueueItem & { display_count?: number };
      return {
        ...item,
        displayedAt: item.displayedAt ?? null,
        isDisplayed: item.displayedAt != null && item.displayedAt !== '',
        displayCount: item.displayCount ?? raw.display_count ?? 0,
      };
    });
  }, []);

  /** Re-sync shuffle "Up next" preview after queue composition changes (all connected producers). */
  const refreshUpNextPreview = useCallback(() => {
    void getCurrentLive().then(({ data }) => {
      if (!data) return;
      dispatch({
        type: 'set_live_prayer_request_id',
        payload: data.livePrayerRequestId != null ? String(data.livePrayerRequestId) : null,
      });
      dispatch({
        type: 'set_live_praise_request_id',
        payload: data.livePraiseRequestId != null ? String(data.livePraiseRequestId) : null,
      });
      dispatch({
        type: 'set_shuffle_prayer_next_request_id',
        payload: data.shufflePrayerNextRequestId != null ? String(data.shufflePrayerNextRequestId) : null,
      });
      dispatch({
        type: 'set_shuffle_praise_next_request_id',
        payload: data.shufflePraiseNextRequestId != null ? String(data.shufflePraiseNextRequestId) : null,
      });
      dispatch({
        type: 'set_shuffle_prayer_next_ids',
        payload: (data.shufflePrayerNextIds ?? []).map((id) => String(id)),
      });
      dispatch({
        type: 'set_shuffle_praise_next_ids',
        payload: (data.shufflePraiseNextIds ?? []).map((id) => String(id)),
      });
      dispatch({
        type: 'set_live_combined_request_id',
        payload: data.liveCombinedRequestId != null ? String(data.liveCombinedRequestId) : null,
      });
      const combinedType = data.liveCombinedQueueType;
      dispatch({
        type: 'set_live_combined_queue_type',
        payload: combinedType === 'prayer' || combinedType === 'praise' ? combinedType : null,
      });
      dispatch({
        type: 'set_shuffle_combined_next_ids',
        payload: (data.shuffleCombinedNextIds ?? []).map((item) => ({
          id: String(item.id),
          queueType: item.queueType,
        })),
      });
    });
  }, []);

  // Fetch queues from API (`both` loads prayer + praise; single-tab mode loads one and clears the other)
  const fetchQueues = useCallback(async () => {
    try {
      setError(null);
      interface QueueResponse {
        items: QueueItem[];
        total: number;
        queueType: string;
      }

      const now = Date.now();
      const guardMs = 5000;
      const isRecentlyRemoved = (id: string) => {
        const t = recentlyRemovedRef.current[id];
        if (t == null) return false;
        if (now - t > guardMs) {
          delete recentlyRemovedRef.current[id];
          return false;
        }
        return true;
      };
      const statusVal = (item: QueueItem) =>
        (typeof item.prayerRequest?.status === 'string'
          ? item.prayerRequest.status
          : (item.prayerRequest?.status as { value?: string })?.value ?? ''
        ).toLowerCase();
      const isQueuedOrDisplayed = (item: QueueItem) => {
        const s = statusVal(item);
        return s === PrayerStatus.QUEUED || s === PrayerStatus.DISPLAYED;
      };

      const fetchPrayer =
        activeQueueOpt === 'prayer' || activeQueueOpt === 'both' || activeQueueOpt === 'combined';
      const fetchPraise =
        activeQueueOpt === 'praise' || activeQueueOpt === 'both' || activeQueueOpt === 'combined';

      if (fetchPrayer && fetchPraise) {
        const [prayerResponse, praiseResponse] = await Promise.all([
          api.get<QueueResponse>('/live-queue/prayer'),
          api.get<QueueResponse>('/live-queue/praise'),
        ]);
        if (prayerResponse.error) throw new Error(prayerResponse.error);
        if (praiseResponse.error) throw new Error(praiseResponse.error);
        let prayerItems = (prayerResponse.data?.items || []).filter(
          (item: QueueItem) => isQueuedOrDisplayed(item) && !isRecentlyRemoved(item.prayerRequestId)
        );
        let praiseItems = (praiseResponse.data?.items || []).filter(
          (item: QueueItem) => isQueuedOrDisplayed(item) && !isRecentlyRemoved(item.prayerRequestId)
        );
        setPrayerQueue(normalizeQueueItems(prayerItems));
        setPraiseQueue(normalizeQueueItems(praiseItems));
      } else if (fetchPrayer) {
        const prayerResponse = await api.get<QueueResponse>('/live-queue/prayer');
        if (prayerResponse.error) throw new Error(prayerResponse.error);
        let prayerItems = (prayerResponse.data?.items || []).filter(
          (item: QueueItem) => isQueuedOrDisplayed(item) && !isRecentlyRemoved(item.prayerRequestId)
        );
        setPrayerQueue(normalizeQueueItems(prayerItems));
        setPraiseQueue([]);
      } else {
        const praiseResponse = await api.get<QueueResponse>('/live-queue/praise');
        if (praiseResponse.error) throw new Error(praiseResponse.error);
        let praiseItems = (praiseResponse.data?.items || []).filter(
          (item: QueueItem) => isQueuedOrDisplayed(item) && !isRecentlyRemoved(item.prayerRequestId)
        );
        setPrayerQueue([]);
        setPraiseQueue(normalizeQueueItems(praiseItems));
      }
      setLastUpdate(new Date());

      // Load shuffle + current-live in the same tick as queue data (avoids empty Live/Up Next until token hydrates).
      try {
        const [shuffleRes, liveRes] = await Promise.all([getShuffleState(), getCurrentLive()]);
        if (shuffleRes.data) {
          setShufflePrayer(shuffleRes.data.prayerEnabled);
          setShufflePraise(shuffleRes.data.praiseEnabled);
          setShuffleCombined(Boolean(shuffleRes.data.combinedEnabled));
          setShufflePrayerIntervalSeconds(shuffleRes.data.prayerIntervalSeconds ?? shuffleRes.data.intervalSeconds ?? 15);
          setShufflePraiseIntervalSeconds(shuffleRes.data.praiseIntervalSeconds ?? shuffleRes.data.intervalSeconds ?? 15);
          setShuffleCombinedIntervalSeconds(
            shuffleRes.data.combinedIntervalSeconds ?? shuffleRes.data.intervalSeconds ?? 15
          );
        }
        if (liveRes.data) {
          setLivePrayerRequestId(liveRes.data.livePrayerRequestId ?? null);
          setLivePraiseRequestId(liveRes.data.livePraiseRequestId ?? null);
          setLiveCombinedRequestId(liveRes.data.liveCombinedRequestId ?? null);
          const ct = liveRes.data.liveCombinedQueueType;
          setLiveCombinedQueueType(ct === 'prayer' || ct === 'praise' ? ct : null);
          setShufflePrayerNextRequestId(liveRes.data.shufflePrayerNextRequestId ?? null);
          setShufflePraiseNextRequestId(liveRes.data.shufflePraiseNextRequestId ?? null);
          setShufflePrayerNextIds(liveRes.data.shufflePrayerNextIds ?? []);
          setShufflePraiseNextIds(liveRes.data.shufflePraiseNextIds ?? []);
          setShuffleCombinedNextIds(
            (liveRes.data.shuffleCombinedNextIds ?? []).map((item) => ({
              id: String(item.id),
              queueType: item.queueType,
            }))
          );
        }
      } catch {
        // Queue rows still valid; producer can refresh or rely on WS for live/up-next.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch queues');
    } finally {
      setIsLoading(false);
    }
  }, [normalizeQueueItems, activeQueueOpt]);

  // Initial fetch and refetch when activeQueue (single-queue mode) changes
  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  // After WebSocket reconnect, immediately resync from API
  useEffect(() => {
    if (reconnectedTick > 0) {
      void fetchQueues();
    }
  }, [reconnectedTick, fetchQueues]);

  // Auto-refresh interval
  useEffect(() => {
    // When the WS is healthy, the backend is already pushing queue updates.
    // Use polling only as a fallback when disconnected (5s for faster recovery).
    if (!autoRefresh || isConnected) return;

    const interval = setInterval(fetchQueues, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchQueues, isConnected]);

  // Handle WebSocket messages (drain full batch so rapid PRAYER_QUEUED + PRAYER_UPDATED are not dropped)
  useEffect(() => {
    const batch = drainQueuedMessages();
    if (batch.length === 0) return;

    for (const msg of batch) {
      const { event, data } = msg;

      switch (event) {
      case WSEventType.PRAYER_QUEUED: {
        // Add new item at top of appropriate queue (ignore non-row payloads)
        const queuedItem = data as QueueItem;
        if (!isFullQueueItemPayload(queuedItem)) break;
        const raw = queuedItem as QueueItem & { display_count?: number };
        const normalizedQueued: QueueItem = {
          ...queuedItem,
          displayedAt: queuedItem.displayedAt ?? null,
          isDisplayed: queuedItem.displayedAt != null && queuedItem.displayedAt !== '',
          displayCount: queuedItem.displayCount ?? raw.display_count ?? 0,
        };
        if (normalizedQueued.queueType === 'prayer') {
          setPrayerQueue((prev) => {
            if (prev.some((item) => item.id === normalizedQueued.id)) return prev;
            return [normalizedQueued, ...prev];
          });
        } else {
          setPraiseQueue((prev) => {
            if (prev.some((item) => item.id === normalizedQueued.id)) return prev;
            return [normalizedQueued, ...prev];
          });
        }
        setNewItemsCount((prev) => prev + 1);
        setLastUpdate(new Date());
        refreshUpNextPreview();
        break;
      }

      case WSEventType.PRAYER_UNQUEUED: {
        // Remove item from queue (backend sends prayer_id and prayerRequestId)
        const unqueuedPayload = data as { prayerRequestId?: string; prayer_id?: string };
        const unqueuedId = unqueuedPayload.prayerRequestId ?? unqueuedPayload.prayer_id;
        if (unqueuedId) {
          recentlyRemovedRef.current[unqueuedId] = Date.now();
          setPrayerQueue(prev => prev.filter(item => item.prayerRequestId !== unqueuedId));
          setPraiseQueue(prev => prev.filter(item => item.prayerRequestId !== unqueuedId));
          setLastUpdate(new Date());
          refreshUpNextPreview();
        }
        break;
      }

      case WSEventType.PRAYER_UPDATED: {
        // Update embedded prayerRequest in queue items and sync queue visibility/display state
        const updatedPrayer = data as PrayerRequest;
        const prayerId = updatedPrayer.id;
        const rawStatus = updatedPrayer.status;
        const statusVal = (rawStatus != null
          ? (typeof rawStatus === 'string' ? rawStatus : (rawStatus as { value?: string })?.value ?? '')
          : ''
        ).toLowerCase();

        // If status changed to Pending or Dismissed (archived), remove from queue
        if (statusVal === PrayerStatus.PENDING || statusVal === PrayerStatus.ARCHIVED) {
          recentlyRemovedRef.current[prayerId] = Date.now();
          setPrayerQueue(prev => prev.filter(item => item.prayerRequestId !== prayerId));
          setPraiseQueue(prev => prev.filter(item => item.prayerRequestId !== prayerId));
          setLastUpdate(new Date());
          refreshUpNextPreview();
          break;
        }

        // Update prayer data and sync displayed state from status
        const updatePrayerInItems = (items: QueueItem[]) =>
          items.map((item) => {
            if (item.prayerRequestId !== prayerId) return item;
            const basePr =
              item.prayerRequest != null && typeof item.prayerRequest === 'object'
                ? item.prayerRequest
                : {};
            const next = {
              ...item,
              prayerRequest: { ...basePr, ...updatedPrayer },
            };
            // Displayed → Queued: show as undisplayed
            if (statusVal === PrayerStatus.QUEUED) {
              next.isDisplayed = false;
              next.displayedAt = null;
            }
            // Queued → Displayed (e.g. manual status change): show as displayed
            if (statusVal === PrayerStatus.DISPLAYED) {
              next.isDisplayed = true;
              next.displayedAt = (updatedPrayer as PrayerRequest & { displayedAt?: string }).displayedAt ?? new Date().toISOString();
            }
            return next;
          });
        setPrayerQueue(prev => updatePrayerInItems(prev));
        setPraiseQueue(prev => updatePrayerInItems(prev));
        setLastUpdate(new Date());
        break;
      }

      case WSEventType.PRAYER_DISPLAYED:
        // Mark item as displayed and update which card is live (so loop + manual set stay in sync)
        const displayedItem = data as QueueItem & {
          prayer_request_id?: string;
          queue_type?: string;
          display_count?: number;
          displayed_at?: string;
        };
        const displayedId = displayedItem.id ?? (displayedItem as { id?: string }).id ?? (displayedItem as { itemId?: string }).itemId;
        const prayerRequestId = displayedItem.prayerRequestId ?? displayedItem.prayer_request_id;
        const queueType = displayedItem.queueType ?? displayedItem.queue_type;
        const newDisplayCount = displayedItem.displayCount ?? displayedItem.display_count;
        const displayedAt = displayedItem.displayedAt ?? displayedItem.displayed_at;
        if (displayedId) {
          const updateDisplayed = (items: QueueItem[]) =>
            items.map(item =>
              item.id === displayedId
                ? {
                    ...item,
                    isDisplayed: true,
                    displayedAt: displayedAt ?? new Date().toISOString(),
                    displayCount: newDisplayCount !== undefined ? newDisplayCount : (item.displayCount ?? 0) + 1,
                  }
                : item
            );
          setPrayerQueue(updateDisplayed);
          setPraiseQueue(updateDisplayed);
          setLastUpdate(new Date());
        }
        // Sync "up next" preview / live ids only when the payload includes them.
        // Prefer explicit live_* fields so combined-only advances do not overwrite
        // the prayer/praise channel heroes.
        // TS: cast through unknown because QueueItem doesn't have an index signature.
        const d = displayedItem as unknown as Record<string, unknown>;
        const livePrayerFromPayload = d.livePrayerRequestId ?? d.live_prayer_request_id;
        const livePraiseFromPayload = d.livePraiseRequestId ?? d.live_praise_request_id;
        if (livePrayerFromPayload !== undefined) {
          setLivePrayerRequestId(livePrayerFromPayload == null ? null : String(livePrayerFromPayload));
          setLiveIsManualOverridePrayer(false);
        } else if (prayerRequestId && queueType === 'prayer') {
          setLivePrayerRequestId(prayerRequestId);
          setLiveIsManualOverridePrayer(false);
        }
        if (livePraiseFromPayload !== undefined) {
          setLivePraiseRequestId(livePraiseFromPayload == null ? null : String(livePraiseFromPayload));
          setLiveIsManualOverridePraise(false);
        } else if (prayerRequestId && queueType === 'praise') {
          setLivePraiseRequestId(prayerRequestId);
          setLiveIsManualOverridePraise(false);
        }

        const spNextReq = d.shufflePrayerNextRequestId ?? d.shuffle_prayer_next_request_id;
        const sprNextReq = d.shufflePraiseNextRequestId ?? d.shuffle_praise_next_request_id;
        const spNextIds = d.shufflePrayerNextIds ?? d.shuffle_prayer_next_ids;
        const sprNextIds = d.shufflePraiseNextIds ?? d.shuffle_praise_next_ids;
        const combinedLive = d.liveCombinedRequestId ?? d.live_combined_request_id;
        const combinedTypeRaw = d.liveCombinedQueueType ?? d.live_combined_queue_type;
        const combinedNext = d.shuffleCombinedNextIds ?? d.shuffle_combined_next_ids;

        if (spNextReq !== undefined) {
          setShufflePrayerNextRequestId(spNextReq == null ? null : String(spNextReq));
        }
        if (sprNextReq !== undefined) {
          setShufflePraiseNextRequestId(sprNextReq == null ? null : String(sprNextReq));
        }
        if (spNextIds !== undefined) {
          setShufflePrayerNextIds(Array.isArray(spNextIds) ? (spNextIds as string[]) : []);
        }
        if (sprNextIds !== undefined) {
          setShufflePraiseNextIds(Array.isArray(sprNextIds) ? (sprNextIds as string[]) : []);
        }
        if (combinedLive !== undefined) {
          setLiveCombinedRequestId(combinedLive == null ? null : String(combinedLive));
        }
        if (combinedTypeRaw !== undefined) {
          const ct = combinedTypeRaw == null ? null : String(combinedTypeRaw);
          setLiveCombinedQueueType(ct === 'prayer' || ct === 'praise' ? ct : null);
        }
        if (combinedNext !== undefined) {
          const list = Array.isArray(combinedNext) ? combinedNext : [];
          setShuffleCombinedNextIds(
            list.map((item: unknown) => {
              const row = item as { id?: string; queueType?: string; queue_type?: string };
              return {
                id: String(row.id ?? ''),
                queueType: row.queueType ?? row.queue_type ?? 'prayer',
              };
            }).filter((item) => item.id)
          );
        }

        if (!displayedId) {
          console.warn('PRAYER_DISPLAYED event received but no ID found in data:', data);
        }
        break;

      case WSEventType.QUEUE_REORDERED:
        // Refresh to get new order; refetch current-live to sync "next in shuffle"
        fetchQueues();
        refreshUpNextPreview();
        break;

      case WSEventType.QUEUE_CLEARED: {
        const clearedPayload = data as {
          queue_type?: string;
          type?: string;
          displayed_only?: boolean;
          displayedOnly?: boolean;
        };
        const clearedType = clearedPayload.queue_type ?? clearedPayload.type;
        const displayedOnlyFlag =
          clearedPayload.displayed_only ?? clearedPayload.displayedOnly;

        const removeDisplayedOnly = (items: QueueItem[]) =>
          items.filter((item) => !item.isDisplayed);

        if (displayedOnlyFlag === true) {
          if (clearedType === 'prayer') {
            setPrayerQueue(removeDisplayedOnly);
          } else if (clearedType === 'praise') {
            setPraiseQueue(removeDisplayedOnly);
          } else {
            setPrayerQueue(removeDisplayedOnly);
            setPraiseQueue(removeDisplayedOnly);
          }
        } else {
          if (clearedType === 'prayer') {
            setPrayerQueue([]);
          } else if (clearedType === 'praise') {
            setPraiseQueue([]);
          } else {
            setPrayerQueue([]);
            setPraiseQueue([]);
          }
        }
        setLastUpdate(new Date());
        refreshUpNextPreview();
        break;
      }

      case WSEventType.SHUFFLE_STATE_CHANGED:
        const shuffleData = data as {
          prayerEnabled?: boolean;
          praiseEnabled?: boolean;
          combinedEnabled?: boolean;
          intervalSeconds?: number;
          prayerIntervalSeconds?: number;
          praiseIntervalSeconds?: number;
          combinedIntervalSeconds?: number;
        };
        if (shuffleData.prayerEnabled !== undefined) setShufflePrayer(shuffleData.prayerEnabled);
        if (shuffleData.praiseEnabled !== undefined) setShufflePraise(shuffleData.praiseEnabled);
        if (shuffleData.combinedEnabled !== undefined) setShuffleCombined(shuffleData.combinedEnabled);
        if (shuffleData.prayerIntervalSeconds !== undefined) setShufflePrayerIntervalSeconds(shuffleData.prayerIntervalSeconds);
        if (shuffleData.praiseIntervalSeconds !== undefined) setShufflePraiseIntervalSeconds(shuffleData.praiseIntervalSeconds);
        if (shuffleData.combinedIntervalSeconds !== undefined) setShuffleCombinedIntervalSeconds(shuffleData.combinedIntervalSeconds);
        if (shuffleData.intervalSeconds !== undefined && shuffleData.prayerIntervalSeconds === undefined) setShufflePrayerIntervalSeconds(shuffleData.intervalSeconds);
        if (shuffleData.intervalSeconds !== undefined && shuffleData.praiseIntervalSeconds === undefined) setShufflePraiseIntervalSeconds(shuffleData.intervalSeconds);
        break;

      case WSEventType.PRAYER_EDITING_STARTED: {
        const editData = data as { prayerId?: string; prayer_id?: string; userId?: string; user_id?: string; userName?: string; user_name?: string };
        const prayerId = editData.prayerId ?? editData.prayer_id;
        const userId = editData.userId ?? editData.user_id ?? '';
        const userName = editData.userName ?? editData.user_name ?? 'Unknown';
        if (prayerId) {
          setEditingBy((prev) => ({ ...prev, [prayerId]: { userId, userName } }));
        }
        break;
      }
      case WSEventType.PRAYER_EDITING_STOPPED: {
        const stopData = data as { prayerId?: string; prayer_id?: string };
        const prayerId = stopData.prayerId ?? stopData.prayer_id;
        if (prayerId) {
          setEditingBy((prev) => {
            const next = { ...prev };
            delete next[prayerId];
            return next;
          });
        }
        break;
      }
      default:
        break;
      }
    }
  }, [messageQueueTick, drainQueuedMessages, fetchQueues, refreshUpNextPreview]);

  // Mark item as displayed
  const markDisplayed = useCallback(async (itemId: string): Promise<boolean> => {
    try {
      const response = await api.post(`/live-queue/${itemId}/displayed`);
      if (response.error) {
        logger.error('API error marking displayed:', {
          error: response.error,
          status: response.status,
          itemId,
        });
        throw new Error(response.error);
      }
      
      // Update local state with response data if available (include displayCount so "Displayed N times" updates immediately)
      const res = response.data as {
        displayedAt?: string;
        displayed_at?: string;
        display_count?: number;
        displayCount?: number;
      } | undefined;
      if (res) {
        const displayCount = res.displayCount ?? res.display_count;
        const displayedAt = res.displayedAt ?? res.displayed_at;
        const updateDisplayed = (items: QueueItem[]) =>
          items.map(item =>
            item.id === itemId
              ? {
                  ...item,
                  isDisplayed: true,
                  displayedAt: displayedAt ?? new Date().toISOString(),
                  displayCount: displayCount !== undefined ? displayCount : (item.displayCount ?? 0) + 1,
                }
              : item
          );
        setPrayerQueue(updateDisplayed);
        setPraiseQueue(updateDisplayed);
      } else {
        // Fallback: optimistic update including displayCount increment
        const updateDisplayed = (items: QueueItem[]) =>
          items.map(item =>
            item.id === itemId
              ? {
                  ...item,
                  isDisplayed: true,
                  displayedAt: new Date().toISOString(),
                  displayCount: (item.displayCount ?? 0) + 1,
                }
              : item
          );
        setPrayerQueue(updateDisplayed);
        setPraiseQueue(updateDisplayed);
      }
      
      return true;
    } catch (err) {
      logger.error('Failed to mark as displayed:', {
        error: err,
        itemId,
        errorMessage: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  }, []);

  // Remove item from queue
  const removeFromQueue = useCallback(async (prayerId: string): Promise<boolean> => {
    try {
      const response = await api.delete(`/live-queue/${prayerId}`);
      if (response.error) {
        throw new Error(response.error);
      }
      
      // Optimistically update local state
      setPrayerQueue(prev => prev.filter(item => item.prayerRequestId !== prayerId));
      setPraiseQueue(prev => prev.filter(item => item.prayerRequestId !== prayerId));
      
      return true;
    } catch (err) {
      logger.error('Failed to remove from queue:', err);
      return false;
    }
  }, []);

  // Clear displayed items only (pending stay); backend default displayed_only=true
  const clearQueue = useCallback(async (queueType: 'prayer' | 'praise'): Promise<boolean> => {
    try {
      const response = await api.delete(`/live-queue/${queueType}/clear?displayed_only=true`);
      if (response.error) {
        throw new Error(response.error);
      }
      const removeDisplayed = (items: QueueItem[]) => items.filter((item) => !item.isDisplayed);
      if (queueType === 'prayer') {
        setPrayerQueue(removeDisplayed);
      } else {
        setPraiseQueue(removeDisplayed);
      }
      return true;
    } catch (err) {
      logger.error('Failed to clear queue:', err);
      return false;
    }
  }, []);

  // Export queue for ProPresenter
  const exportQueue = useCallback(async (
    type: 'prayer' | 'praise' | 'all',
    exportOptions: ExportOptions = {}
  ): Promise<string> => {
    const { groupByCategory = false, includeCategoryLabels = true } = exportOptions;
    
    try {
      const params = new URLSearchParams();
      if (groupByCategory) params.set('group_by_category', 'true');
      if (includeCategoryLabels) params.set('include_labels', 'true');
      
      const endpoint = `/producer/export/${type}?${params.toString()}`;
      const response = await api.get<{ text: string }>(endpoint);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data?.text || formatQueueForExport(type, groupByCategory, includeCategoryLabels);
    } catch (err) {
      // Fallback to local formatting if API fails
      return formatQueueForExport(type, groupByCategory, includeCategoryLabels);
    }
  }, [prayerQueue, praiseQueue, prayerQueueGrouped, praiseQueueGrouped]);

  // Local formatting fallback
  const formatQueueForExport = useCallback((
    type: 'prayer' | 'praise' | 'all',
    groupByCategory: boolean,
    includeCategoryLabels: boolean
  ): string => {
    const formatItem = (item: QueueItem): string => {
      const pr = item.prayerRequest;
      if (!pr) return '';
      const name = pr.isAnonymous ? 'Anonymous' : (formatFullName(pr.firstName, pr.lastName) || 'Anonymous');
      return `${pr.body}\n- ${name}`;
    };

    const formatGrouped = (grouped: GroupedQueueItems): string => {
      return Object.entries(grouped)
        .map(([category, items]) => {
          const header = includeCategoryLabels ? `[${category}]\n` : '';
          const itemsText = items.map(formatItem).join('\n\n');
          return header + itemsText;
        })
        .join('\n\n---\n\n');
    };

    const formatList = (items: QueueItem[]): string => {
      return items.map(formatItem).join('\n\n');
    };

    let output = '';

    if (type === 'prayer' || type === 'all') {
      if (type === 'all') output += '=== PRAYER REQUESTS ===\n\n';
      output += groupByCategory 
        ? formatGrouped(prayerQueueGrouped) 
        : formatList(prayerQueue);
    }

    if (type === 'all') {
      output += '\n\n=== ANSWERED PRAYERS ===\n\n';
    }

    if (type === 'praise' || type === 'all') {
      output += groupByCategory 
        ? formatGrouped(praiseQueueGrouped) 
        : formatList(praiseQueue);
    }

    return output;
  }, [prayerQueue, praiseQueue, prayerQueueGrouped, praiseQueueGrouped]);

  /** Get export text synchronously so clipboard copy can run in same user gesture. */
  const getExportText = useCallback((
    type: 'prayer' | 'praise' | 'all',
    exportOptions: ExportOptions = {}
  ): string => {
    const { groupByCategory = false, includeCategoryLabels = true } = exportOptions;
    return formatQueueForExport(type, groupByCategory, includeCategoryLabels);
  }, [formatQueueForExport]);

  const clearNewItems = useCallback(() => {
    setNewItemsCount(0);
  }, []);

  const setShuffleState = useCallback(async (update: ShuffleStateUpdate) => {
    const { data, error } = await putShuffleState(update);
    if (error) {
      logger.error('putShuffleState failed:', error);
      return;
    }
    if (data) {
      setShufflePrayer(data.prayerEnabled);
      setShufflePraise(data.praiseEnabled);
      setShuffleCombined(Boolean(data.combinedEnabled));
      setShufflePrayerIntervalSeconds(data.prayerIntervalSeconds ?? data.intervalSeconds ?? 15);
      setShufflePraiseIntervalSeconds(data.praiseIntervalSeconds ?? data.intervalSeconds ?? 15);
      setShuffleCombinedIntervalSeconds(data.combinedIntervalSeconds ?? data.intervalSeconds ?? 15);
    }
  }, []);

  return {
    prayerQueue,
    praiseQueue,
    prayerQueueGrouped,
    praiseQueueGrouped,
    isLoading,
    error,
    isConnected,
    connectionState,
    lastUpdate,
    newItemsCount,
    markDisplayed,
    removeFromQueue,
    clearQueue,
    refresh: fetchQueues,
    clearNewItems,
    exportQueue,
    getExportText,
    shufflePrayer,
    shufflePraise,
    shuffleCombined,
    shufflePrayerIntervalSeconds,
    shufflePraiseIntervalSeconds,
    shuffleCombinedIntervalSeconds,
    setShuffleState,
    livePrayerRequestId,
    livePraiseRequestId,
    liveCombinedRequestId,
    liveCombinedQueueType,
    shufflePrayerNextRequestId,
    shufflePraiseNextRequestId,
    shufflePrayerNextIds,
    shufflePraiseNextIds,
    shuffleCombinedNextIds,
    liveIsManualOverridePrayer,
    liveIsManualOverridePraise,
    setLivePrayerRequestId,
    setLivePraiseRequestId,
    setLiveCombinedRequestId,
    markLiveAsManualOverridePrayer: () => setLiveIsManualOverridePrayer(true),
    markLiveAsManualOverridePraise: () => setLiveIsManualOverridePraise(true),
    editingBy,
    sendMessage,
    reconnectedTick,
    lastPongAt,
  };
}

export default useProducerQueue;
